import {
  EmployeesReportColumn,
  EmployeesReportDistinctValuesDto,
  EmployeesReportRowDto,
  EmployeesReportView,
  EmployeesReportViewCountsDto,
  Meta,
  SortDirection,
} from '@zorro/clients';
import { useDebouncedValue, useMonolithQuery } from '@zorro/web/ui-utils';
import { parseAsStringEnum, useQueryState } from 'nuqs';
import { Dispatch, SetStateAction, useEffect, useMemo, useState } from 'react';

import { EmployeesDatatableStoredParams } from './employeesDatatable.types';
import { toEmployeesReportFilters } from './employeesReportFilters';
import { getInitialEmployeesTableFilters } from './initialEmployeesTableFilters';

type EmployeesTableState = Omit<EmployeesDatatableStoredParams, 'view'>;

const defaultEmployeesTableState: EmployeesTableState = {
  filters: [],
  searchValue: '',
  page: 1,
  sortStatus: {
    columnAccessor: EmployeesReportColumn.employeeFullName,
    direction: SortDirection.asc,
  },
  pageSize: 50,
};

export const defaultQueryParams: EmployeesDatatableStoredParams = {
  view: EmployeesReportView.ALL,
  ...defaultEmployeesTableState,
};

const employeesReportViewParser = parseAsStringEnum(
  Object.values(EmployeesReportView)
).withDefault(EmployeesReportView.ALL);

function useInitialEmployeesTableFilters() {
  const [initialEnrollmentStatus, setInitialEnrollmentStatus] = useQueryState(
    'initialEnrollmentStatus'
  );
  const [initialFilters] = useState(() =>
    getInitialEmployeesTableFilters(initialEnrollmentStatus)
  );

  useEffect(() => {
    if (initialEnrollmentStatus !== null) {
      void setInitialEnrollmentStatus(null, { history: 'replace' });
    }
  }, [initialEnrollmentStatus, setInitialEnrollmentStatus]);

  return initialFilters;
}

function useEmployeesDatatableParams(employerId: string) {
  const [view, setView] = useQueryState('view', employeesReportViewParser);
  const initialFilters = useInitialEmployeesTableFilters();
  const [tableState, setTableState] = useState<EmployeesTableState>(() => ({
    ...defaultEmployeesTableState,
    filters: initialFilters,
  }));

  // Reset during render, not in an effect,
  // so the previous employer's table never reaches the screen.
  const [tableEmployerId, setTableEmployerId] = useState(employerId);
  if (tableEmployerId !== employerId) {
    setTableEmployerId(employerId);
    setTableState(defaultEmployeesTableState);
  }

  const employeesDatatableParams: EmployeesDatatableStoredParams = {
    view,
    ...tableState,
  };

  // view lives in the URL, so updates resolve against this render's params.
  // Two calls in one event don't compose.
  const setEmployeesDatatableParams = (
    update: SetStateAction<EmployeesDatatableStoredParams>
  ) => {
    const { view: nextView, ...nextTableState } =
      typeof update === 'function' ? update(employeesDatatableParams) : update;
    setTableState(nextTableState);
    if (nextView !== view) {
      void setView(nextView);
    }
  };

  return { employeesDatatableParams, setEmployeesDatatableParams };
}

export function usePaginatedEmployeeReports(
  employerId: string,
  selectedEnrollmentPeriodId: string
): {
  isLoading: boolean;
  pageRecords: EmployeesReportRowDto[];
  counts?: EmployeesReportViewCountsDto;
  filterOptions?: EmployeesReportDistinctValuesDto[];
  meta?: Meta;
  employeesDatatableParams: EmployeesDatatableStoredParams;
  setEmployeesDatatableParams: Dispatch<
    SetStateAction<EmployeesDatatableStoredParams>
  >;
} {
  const { employeesDatatableParams, setEmployeesDatatableParams } =
    useEmployeesDatatableParams(employerId);
  const { view, sortStatus, filters, searchValue, page, pageSize } =
    employeesDatatableParams;

  const { debounced: debouncedFilters } = useDebouncedValue({
    value: filters,
    wait: 300,
  });
  const { debounced: debouncedSearchValue } = useDebouncedValue({
    value: searchValue,
    wait: 300,
  });
  const { debounced: debouncedSortStatus } = useDebouncedValue({
    value: sortStatus,
    wait: 150,
  });
  const filterBy = useMemo(
    () => toEmployeesReportFilters(debouncedFilters),
    [debouncedFilters]
  );

  const { data: paginatedResponse, isFetching } = useMonolithQuery({
    method: 'employeeReportsControllerGenerateEmployeesReport',
    params: [
      employerId,
      selectedEnrollmentPeriodId,
      {
        view,
        sortBy: debouncedSortStatus.columnAccessor as EmployeesReportColumn,
        sortDirection: debouncedSortStatus.direction as SortDirection,
        filterBy,
        searchTerm: debouncedSearchValue,
        perPage: pageSize,
        page,
      },
    ],
    enabled: Boolean(employerId && selectedEnrollmentPeriodId),
  });

  return {
    isLoading: isFetching || !selectedEnrollmentPeriodId,
    pageRecords: paginatedResponse?.data ?? [],
    counts: paginatedResponse?.counts,
    filterOptions: paginatedResponse?.filterOptions,
    meta: paginatedResponse?.meta,
    employeesDatatableParams,
    setEmployeesDatatableParams,
  };
}
